import React, { useRef, useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis } from 'recharts';
import { Download, Copy, Check, Music, Users, BarChart3, ExternalLink, Trophy, Clock, TrendingUp } from 'lucide-react';
import { Button } from './ui/button';
import html2canvas from 'html2canvas';
import { toast } from 'sonner';

import { API_URL } from '../utils/api';

const LEVEL_COLORS = {
  'Absolute Beginner': '#22c55e',
  'Beginner': '#84cc16',
  'Improver': '#eab308',
  'Intermediate': '#f97316',
  'Advanced': '#ef4444',
  'Unknown': '#94a3b8'
};

export default function SessionSummary({ session, clubLogo, clubName, onClose }) {
  const infographicRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);

  // Fetch detailed analytics when component mounts
  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!session?.session_id) return;
      
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${API_URL}/api/dj/analytics/session-stats/${session.session_id}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (response.ok) {
          const data = await response.json();
          setAnalytics(data);
        }
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLoadingAnalytics(false);
      }
    };
    
    fetchAnalytics();
  }, [session?.session_id]);

  const playedDances = session?.played_dances || [];
  const totalRequests = analytics?.stats?.total_requests || 
    ((session?.pending_requests?.length || 0) + playedDances.length);
  
  // Calculate level distribution
  const levelCounts = {};
  playedDances.forEach(dance => {
    const level = dance.level || 'Unknown';
    levelCounts[level] = (levelCounts[level] || 0) + 1;
  });
  
  const pieData = Object.entries(levelCounts).map(([name, value]) => ({
    name,
    value,
    color: LEVEL_COLORS[name] || LEVEL_COLORS['Unknown']
  }));

  // Generate playlist text with hyperlinks (HTML format)
  const generatePlaylistHTML = () => {
    let html = `<h3>${session?.session_name || 'DJ Session'} - ${clubName}</h3>\n`;
    html += `<p>${new Date(session?.started_at).toLocaleDateString('en-GB', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    })}</p>\n\n`;
    html += `<ol>\n`;
    
    playedDances.forEach((dance, i) => {
      const name = dance.dance_name || 'Unknown Dance';
      const choreo = dance.choreographer || '';
      const url = dance.dance_url;
      
      if (url) {
        html += `  <li><a href="${url}" target="_blank">${name}</a>${choreo ? ` - ${choreo}` : ''}</li>\n`;
      } else {
        html += `  <li>${name}${choreo ? ` - ${choreo}` : ''}</li>\n`;
      }
    });
    
    html += `</ol>\n`;
    html += `<p><em>Generated with Line Dance Central</em></p>`;
    return html;
  };

  // Generate plain text playlist
  const generatePlaylistText = () => {
    let text = `${session?.session_name || 'DJ Session'} - ${clubName}\n`;
    text += `${new Date(session?.started_at).toLocaleDateString('en-GB', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    })}\n\n`;
    text += `Dances Played:\n`;
    
    playedDances.forEach((dance, i) => {
      const name = dance.dance_name || 'Unknown Dance';
      const choreo = dance.choreographer || '';
      const url = dance.dance_url;
      
      text += `${i + 1}. ${name}${choreo ? ` - ${choreo}` : ''}${url ? ` (${url})` : ''}\n`;
    });
    
    text += `\nGenerated with Line Dance Central - linedancecentral.uk`;
    return text;
  };

  const copyPlaylist = async () => {
    try {
      await navigator.clipboard.writeText(generatePlaylistText());
      setCopied(true);
      toast.success('Playlist copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const downloadInfographic = async () => {
    if (!infographicRef.current) return;
    
    setDownloading(true);
    try {
      const canvas = await html2canvas(infographicRef.current, {
        backgroundColor: '#1e1b4b',
        scale: 2,
        logging: false,
        useCORS: true
      });
      
      const link = document.createElement('a');
      link.download = `${clubName.replace(/\s+/g, '-')}-session-${new Date(session?.started_at).toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success('Infographic downloaded!');
    } catch (err) {
      toast.error('Failed to download infographic');
      console.error(err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-slate-900 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-slate-900 border-b border-slate-700 p-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold text-white">Session Summary</h2>
          <Button variant="ghost" onClick={onClose} className="text-slate-400 hover:text-white">
            Close
          </Button>
        </div>

        <div className="p-6 space-y-8">
          {/* Playlist Section */}
          <div className="bg-slate-800 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Music className="w-5 h-5 text-violet-400" />
                Playlist ({playedDances.length} dances)
              </h3>
              <Button
                onClick={copyPlaylist}
                size="sm"
                className="bg-violet-600 hover:bg-violet-500"
              >
                {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                {copied ? 'Copied!' : 'Copy Playlist'}
              </Button>
            </div>
            
            <div className="max-h-64 overflow-y-auto space-y-2">
              {playedDances.length === 0 ? (
                <p className="text-slate-400 text-sm">No dances were played in this session.</p>
              ) : (
                playedDances.map((dance, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm">
                    <span className="text-slate-400 w-6 text-right">{i + 1}.</span>
                    {dance.dance_url ? (
                      <a 
                        href={dance.dance_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-violet-400 hover:text-violet-300 flex items-center gap-1"
                      >
                        {dance.dance_name}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <span className="text-white">{dance.dance_name}</span>
                    )}
                    {dance.choreographer && (
                      <span className="text-slate-400">- {dance.choreographer}</span>
                    )}
                    {dance.level && (
                      <span 
                        className="text-xs px-2 py-0.5 rounded-full ml-auto"
                        style={{ backgroundColor: LEVEL_COLORS[dance.level] || LEVEL_COLORS['Unknown'], color: '#000' }}
                      >
                        {dance.level}
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Infographic Section */}
          <div className="bg-slate-800 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-violet-400" />
                Session Infographic
              </h3>
              <Button
                onClick={downloadInfographic}
                disabled={downloading}
                size="sm"
                className="bg-green-600 hover:bg-green-500"
              >
                <Download className="w-4 h-4 mr-1" />
                {downloading ? 'Generating...' : 'Download PNG'}
              </Button>
            </div>

            {/* Infographic Canvas */}
            <div 
              ref={infographicRef} 
              className="bg-gradient-to-br from-indigo-950 to-slate-900 rounded-xl p-6 text-white"
              style={{ minWidth: '500px' }}
            >
              {/* Club Header */}
              <div className="flex items-center gap-4 mb-6 pb-4 border-b border-slate-700">
                {clubLogo && (
                  <img 
                    src={clubLogo} 
                    alt={clubName}
                    className="w-16 h-16 rounded-xl object-cover"
                    crossOrigin="anonymous"
                  />
                )}
                <div>
                  <h4 className="text-xl font-bold">{clubName}</h4>
                  <p className="text-slate-400 text-sm">
                    {new Date(session?.started_at).toLocaleDateString('en-GB', { 
                      weekday: 'long', 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    })}
                  </p>
                  {session?.session_name && (
                    <p className="text-violet-400 text-sm font-medium">{session.session_name}</p>
                  )}
                </div>
              </div>

              {/* Main Stats Grid */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-slate-800/50 rounded-xl p-4 text-center">
                  <Users className="w-8 h-8 text-violet-400 mx-auto mb-2" />
                  <p className="text-3xl font-bold">{totalRequests}</p>
                  <p className="text-slate-400 text-sm">Total Requests</p>
                </div>
                <div className="bg-slate-800/50 rounded-xl p-4 text-center">
                  <Music className="w-8 h-8 text-green-400 mx-auto mb-2" />
                  <p className="text-3xl font-bold">{playedDances.length}</p>
                  <p className="text-slate-400 text-sm">Dances Played</p>
                </div>
              </div>

              {/* Additional Stats Row */}
              {analytics && (
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-slate-800/50 rounded-xl p-3 text-center">
                    <TrendingUp className="w-6 h-6 text-teal-400 mx-auto mb-1" />
                    <p className="text-xl font-bold">{analytics.stats?.acceptance_rate || 0}%</p>
                    <p className="text-slate-400 text-xs">Acceptance Rate</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-xl p-3 text-center">
                    <Clock className="w-6 h-6 text-orange-400 mx-auto mb-1" />
                    <p className="text-xl font-bold">{analytics.stats?.total_declined || 0}</p>
                    <p className="text-slate-400 text-xs">Declined</p>
                  </div>
                </div>
              )}

              {/* Most Requested Dance */}
              {analytics?.most_requested_dance && (
                <div className="bg-gradient-to-r from-yellow-900/30 to-amber-900/30 rounded-xl p-4 mb-4 border border-yellow-700/30">
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className="w-5 h-5 text-yellow-400" />
                    <span className="text-yellow-400 font-semibold text-sm">Most Requested Dance</span>
                  </div>
                  <p className="text-white font-bold text-lg">{analytics.most_requested_dance.name}</p>
                  <p className="text-slate-400 text-sm">{analytics.most_requested_dance.count} requests</p>
                </div>
              )}

              {/* Top Requesters */}
              {analytics?.top_requesters && analytics.top_requesters.length > 0 && (
                <div className="bg-slate-800/50 rounded-xl p-4 mb-4">
                  <h5 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Top Requesters
                  </h5>
                  <div className="space-y-2">
                    {analytics.top_requesters.slice(0, 3).map((requester, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <span className="text-slate-300">
                          {i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'} {requester.name}
                        </span>
                        <span className="text-violet-400 font-medium">{requester.count} requests</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pie Chart */}
              {pieData.length > 0 && (
                <div className="bg-slate-800/50 rounded-xl p-4">
                  <h5 className="text-center text-sm font-medium text-slate-300 mb-2">Dance Levels</h5>
                  <div style={{ height: '200px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={70}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                          labelLine={false}
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px' }}
                          itemStyle={{ color: '#fff' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="mt-6 pt-4 border-t border-slate-700 flex items-center justify-center gap-2 text-slate-400 text-xs">
                <span>Powered by</span>
                <span className="font-semibold text-violet-400">Line Dance Central</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
